import DocIcon from 'assets/icons/DocIcon';
import GalleryIcon from 'assets/icons/GalleryIcon';
import RupeeIcon from 'assets/icons/RupeeIcon';
import FieldInstruction from 'Components/Common/FieldInstruction';
import RichTextEditor from 'Components/Common/RichTextEditor';
import RadioGroup from 'Components/input/RadioGroup/RadioGroup';
import { FIELDS_INSTRUCTIONS } from 'constants/fieldsInstructions';
import { GENERAL_GIGTYPE_ID, REPOST_GIGTYPE_ID } from 'constants/milestone';
import { noop } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react'
import { FormGroup, Col, Label, Row, Input, InputGroup, Button, Card, CardHeader, CardTitle, CardBody, Modal, ModalBody } from 'reactstrap';
import CampaignGig from '../milestone/components/CampaignGig';
import { SelectedStore } from '../milestone/gigs/Stores';
import { CampaignFile, GigType, Milestone } from 'types/campaign/collaborations';
import FormField from 'Components/Common/FormField';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { AppDispatch, RootState } from 'store';
import AddCampaignGig from './AddCampaignGig';
import { updateMilestoneCTAs, updateMilestoneGigs } from 'services/api/campaign';
import { fetchCampaignGigsThunk } from 'slices/campaigns/milestones/thunk';
import TalkingPointsEditor from './TalkingPointsEditor';
import MilestoneScript from '../milestone/components/MilestoneScript';
import MilestoneCaption from '../milestone/components/MilestoneCaption';
import ListEditor from './ListEditor';
import CreatableTags from 'Components/Common/CreatableTags';
import { getMediaTypeFromURL } from 'utils/helpers';
import CloseButton from 'Components/Common/CloseButton';
import CollapsableContent from 'Components/Common/CollapsableContent';
import HTMLContent from 'Components/Common/HTMLContent';
import AddCTA, { CTA, CTAToBeEditedInterface } from './AddCTA';
import TalkingPoints from './TalkingPoints';
import AccessControl from 'wrappers/AccessControl';
import { PRIVILEGE } from 'constants/privilege';
import CustomMultiSelect from 'Components/Common/CustomMultiSelect';
import Select from 'react-select'
interface MilestoneFormProps {
    milestone: Milestone, handleMilestoneChange: (key: string, value: any) => void, errors: Record<string, string>, activeMilestone: Milestone, gigTypes: GigType[], milestoneGigType: GigType,
    updateMilestone: (data: Milestone) => void,
    disabled?: boolean
}

const MilestoneForm: React.FC<MilestoneFormProps> = ({ milestone, handleMilestoneChange, errors, activeMilestone, gigTypes, milestoneGigType, updateMilestone, disabled = false }) => {

    const { campaignId } = useParams();
    const selectedAgencyId = useSelector((state: RootState) => state.auth.selectedAgencyId);
    const dispatch: AppDispatch = useDispatch();
    const campaignMilestones = useSelector((state: RootState) => state.campaignMilestones.items[campaignId!])
    const [milestoneGigsUpdateStatus, setMilestoneGigsUpdateStatus] = useState<null | { status: 'success' | 'danger', message: string }>(null);
    const campaignMilestonesLoading = useSelector((state: RootState) => state.campaignMilestones.loading);
    const [loading, setLoading] = useState(true);
    const [selectedMedia, setSelectedMedia] = useState<any>(null);

    const [selectedGigId, setSelectedGigId] = useState<number | null>(null);
    const [addGigModalOpen, setAddGigModalOpen] = useState(false);

    const [addCTAModalOpen, setAddCTAModalOpen] = useState(false);
    const [CTAToBeEdited, setCTAToBeEdited] = useState<CTAToBeEditedInterface | null>(null);

    const milestonesData = campaignMilestones?.milestonesData;


    const [sampleScriptsAllowed, allowSampleScripts] = useState(false);
    const [sampleCaptionsAllowed, allowSampleCaptions] = useState(false);

    const campaignGigs = useSelector((state: RootState) => state.campaignMilestones.gigs[campaignId!]?.items || []);

    const gigObjectName = (gig_type_id: number) => {
        return gigTypes.find((g: any) => g.id === gig_type_id)?.object_name;
    };


    const milestoneGigs = (milestone: Milestone) => {
        return campaignGigs.filter((gig) => milestone.campaign_gig_ids.includes(gig.id));
    };

    const getGigType = (gigTypeId: number) => {
        return gigTypes.find((g) => g.id === gigTypeId) || null;
    }

    const getGigTypeSlug = (gigTypeId: number) => {
        return getGigType(gigTypeId)?.slug || '';
    }


    const attachCampaignGig = async (gigId: number) => {

        const milestoneId = milestone?.id;

        if (!campaignId || !selectedAgencyId || !milestoneId) return;

        const currentCampaignGigs = Array.isArray(milestone?.campaign_gig_ids) ? [...milestone?.campaign_gig_ids] : [];
        const updatedCampaignGig = !currentCampaignGigs.includes(gigId) ? [gigId, ...currentCampaignGigs] : currentCampaignGigs;

        try {
            await updateMilestoneGigs(selectedAgencyId, campaignId, milestoneId, updatedCampaignGig);
            await dispatch(fetchCampaignGigsThunk(campaignId));
            updateMilestone({ ...milestone, campaign_gig_ids: updatedCampaignGig });
            setMilestoneGigsUpdateStatus({ status: 'success', message: `${getGigType(milestone?.gig_type_id)?.object_name} added successfully!` });
            setAddGigModalOpen(false);
            setSelectedGigId(null);
        } catch (error: any) {
            setMilestoneGigsUpdateStatus({ status: 'danger', message: error?.response?.data?.message ?? `Failed to add ${getGigType(milestone?.gig_type_id)?.object_name}` });
        }
    }

    const attachCampaignCTA = async (cta: CTA) => {

        const milestoneId = milestone?.id;

        if (!campaignId || !selectedAgencyId || !milestoneId) return;

        const currentCTAs = Array.isArray(milestone?.specifications?.cta) ? [...milestone.specifications.cta] : [];
        const updatedCTAs = [...currentCTAs, cta];

        try {
            await updateMilestoneCTAs(selectedAgencyId, campaignId, milestoneId, updatedCTAs);
            updateMilestone({ ...milestone, specifications: { ...milestone.specifications, cta: updatedCTAs } });
            setAddCTAModalOpen(false);
        } catch (error: any) {
            console.log(error);
        }
    }

    useEffect(() => {
        if (activeMilestone && Array.isArray(activeMilestone.specifications?.sample_captions) && activeMilestone.specifications.sample_captions.length > 0) {
            allowSampleCaptions(true);
        } else {
            allowSampleCaptions(false);
        }
        if (activeMilestone && Array.isArray(activeMilestone.specifications?.sample_scripts) && activeMilestone.specifications.sample_scripts.length > 0) {
            allowSampleScripts(true);
        } else {
            allowSampleScripts(false);
        }
    }, [activeMilestone])


    const getCreatives = (): CampaignFile[] => {
        if (milestone && Array.isArray(milestone.campaign_files)) {
            return milestone.campaign_files.filter((cf) => cf.type === 'creative');
        }

        return [];
    }

    const getSamplePosts = (): CampaignFile[] => {
        if (milestone && Array.isArray(milestone.campaign_files)) {
            return milestone.campaign_files.filter((cf) => cf.type === 'sample_post');
        }

        return [];
    }

    const canSetDMAutomation = useMemo(() => {
        if (milestone?.specifications?.cta && Array.isArray(milestone.specifications.cta) && milestone.specifications.cta.length > 0) {
            return milestone.specifications.cta.some((cta) => cta.url);
        }
        return false;
    }, [milestone])

    return (
        <>
            <FormGroup row className='py-4 mt-3'> {/* Brief */}
                <Col sm={4} className='my-1'>
                    <div className="d-flex gap-3">
                        <div className="w-40px">
                            <DocIcon className='me-3' />
                        </div>
                        <div className="d-flex flex-column gap-2">
                            <Label htmlFor='name' className='form-label input-label mb-0'>
                                Milestone Brief
                                <span className="text-danger"> *</span>
                            </Label>
                            <FieldInstruction instruction={FIELDS_INSTRUCTIONS.MILESTONE.BRIEF} />
                        </div>
                    </div>
                </Col>
                <Col sm={8} className='my-1'>
                    <div className="card-gray">
                        <CollapsableContent noPaddingReadMoreButton>
                            <HTMLContent content={milestone.brief ?? '-'} />
                        </CollapsableContent>
                    </div>

                    {
                        errors.brief &&
                        <span className="text-danger mt-2" id='error-brief'>{errors.brief}</span>
                    }
                </Col>
            </FormGroup>
            {
                activeMilestone?.gig_type_id === GENERAL_GIGTYPE_ID && false && //Hidden in readonly mode
                <>
                    <div className="divider"></div>
                    <FormGroup row className='py-4 mt-3'> {/* Type of Milestone */}
                        <Col sm={4} className='my-1'>
                            <div className="d-flex gap-3 align-items-center">
                                <div className="w-40px">
                                    <DocIcon className='me-3' />
                                </div>
                                <div className="d-flex flex-column gap-2">
                                    <Label htmlFor='name' className='form-label input-label mb-0'>
                                        Type of Milestone
                                        <span className="text-danger"> *</span>
                                    </Label>
                                    {/* <FieldInstruction instruction={FIELDS_INSTRUCTIONS.CAMPAIGN.DESCRIPTION} /> */}
                                </div>
                            </div>
                        </Col>
                        <Col sm={8} className='my-1'>
                            <select
                                disabled={milestone.campaign_gig_ids.length > 0 || disabled}
                                className="form-select"
                                value={milestone.gig_type_id || ''}
                                onChange={(e) => handleMilestoneChange('gig_type_id', Number(e.target.value) ? Number(e.target.value) : '')}
                            >
                                <option value="">Select Milestone Type</option>
                                {
                                    gigTypes.map((gigType) => (
                                        <option key={gigType.id} value={gigType.id}>
                                            {gigType.name}
                                        </option>
                                    ))
                                }
                            </select>
                            {
                                errors.gig_type_id &&
                                <span className="text-danger mt-2" id='error-gig_type_id'>{errors.gig_type_id}</span>
                            }
                        </Col>
                    </FormGroup>
                </>
            }
            <div className="divider"></div>
            <FormGroup row className='py-4 mt-3'> {/* Requirements */}
                <Col sm={4} className='my-1'>
                    <div className="d-flex gap-3 align-items-center">
                        <div className="w-40px">
                            <span className="icon-circle me-3">
                                <GalleryIcon />
                            </span>
                        </div>
                        <div className="d-flex flex-column gap-2">
                            <Label htmlFor='name' className='form-label input-label mb-0'>
                                Media Requirements
                                <span className="text-danger"> *</span>
                            </Label>
                            {/* <FieldInstruction instruction={FIELDS_INSTRUCTIONS.CAMPAIGN.CONTENT_MEDIA_TYPES} /> */}
                        </div>
                    </div>
                </Col>
                <Col sm={8} className='my-1'>
                    <Row>
                        <Col>
                            <FormGroup>
                                <Label htmlFor='name' className='form-label mb-2'>
                                    No. of Reels
                                    <span className="text-danger"> *</span>
                                </Label>
                                <Input
                                    type='number'
                                    name='REELS'
                                    className='form-control'
                                    disabled={disabled}
                                    onChange={(e) => {
                                        let contentMediaTypes = { ...milestone.content_media_types, REELS: e.target.value };
                                        handleMilestoneChange('content_media_types', contentMediaTypes)
                                    }}
                                    value={milestone.content_media_types.REELS || ""}
                                    placeholder='Enter number of reels' />
                            </FormGroup>
                        </Col>
                        <Col>
                            <FormGroup>
                                <Label htmlFor='name' className='form-label mb-2'>
                                    No. of Posts
                                </Label>

                                <Input
                                    type='number'
                                    name='FEED'
                                    disabled={disabled}
                                    className='form-control'
                                    onChange={(e) => handleMilestoneChange('content_media_types', { ...milestone.content_media_types, FEED: e.target.value })}
                                    value={milestone.content_media_types.FEED || ""}
                                    placeholder='Enter number of posts' />
                            </FormGroup>
                        </Col>
                        <Col>
                            <FormGroup>
                                <Label htmlFor='name' className='form-label mb-2'>
                                    No. of Stories
                                </Label>
                                <Input
                                    type='number'
                                    name='STORY'
                                    className='form-control'
                                    disabled={disabled}
                                    onChange={(e) => {
                                        let contentMediaTypes = { ...milestone.content_media_types, STORY: e.target.value };
                                        handleMilestoneChange('content_media_types', contentMediaTypes)
                                    }}
                                    value={milestone.content_media_types.STORY || ""}
                                    placeholder='Enter number of stories' />
                            </FormGroup>
                        </Col>
                    </Row>
                    {
                        errors.content_media_types &&
                        <span className="text-danger mt-2" id='error-content_media_types'>{errors.content_media_types}</span>
                    }
                </Col>
            </FormGroup>
            <div className="divider"></div>
            <FormGroup row className='py-4 mt-3'> {/* Payout */}
                <Col sm={4} className='my-1'>
                    <div className="d-flex gap-3">
                        <div className="w-40px">
                            <span className="icon-circle me-3"><RupeeIcon /></span>
                        </div>
                        <div className="d-flex flex-column gap-2 align-items-center">
                            <Label htmlFor='name' className='form-label input-label mb-0'>
                                Milestone Payout
                                <span className="text-danger"> *</span>
                                <span className="d-block input-label-subtext">Exlcluding Reimbursement (if any)</span>
                            </Label>
                            {/* <FieldInstruction instruction={FIELDS_INSTRUCTIONS.CAMPAIGN.DESCRIPTION} /> */}
                        </div>
                    </div>
                </Col>
                <Col sm={8} className='my-1'>
                    <Input
                        type='number'
                        value={milestone.gig_amount || ''}
                        onChange={(e) => handleMilestoneChange('gig_amount', Number(e.target.value) ? Number(e.target.value) : '')}
                        placeholder='Eg: ₹3300'
                        disabled={disabled}

                    />
                    {
                        errors.gig_amount &&
                        <span className="text-danger mt-2" id='error-gig_amount'>{errors.gig_amount}</span>
                    }
                </Col>
            </FormGroup>
            {
                milestoneGigType?.has_gigs &&
                <>
                    {
                        milestoneGigType?.slug === 'store_visit' ? (
                            <>
                                <FormGroup row className='py-2 mt-3'> {/* Purchase required */}
                                    <Col sm={4} className='my-1'>
                                        <div className="d-flex gap-3">
                                            <div className="w-40px">
                                                <span className="icon-circle me-3"><DocIcon /></span>
                                            </div>
                                            <div className="d-flex flex-column gap-2">
                                                <Label htmlFor='name' className='form-label input-label mb-0'>
                                                    {milestonesData?.properties?.purchase_required?.label}
                                                    <span className="text-danger"> *</span>
                                                </Label>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col sm={8} className='my-1'>
                                        <div className="mb-3">
                                            <RadioGroup
                                                name="purchase_required"
                                                disabled={disabled}
                                                options={[{ label: 'Sponsored', id: 'sponsored' }, { label: 'Influencer Self Purchase', id: 'self_purchase' }]}
                                                defaultValue={milestone.specifications?.purchase_required ? 'self_purchase' : 'sponsored'}
                                                onChange={(val) => {
                                                    // const specifications = { ...milestone.specifications, purchase_required: (val === 'self_purchase') }
                                                    // handleMilestoneChange(index, 'specifications', specifications);
                                                }}
                                            />
                                        </div>

                                    </Col>
                                </FormGroup>
                                <FormGroup row className='py-2 mt-3'> {/* Reimbursement Budget */}
                                    <Col sm={4} className='my-1'>
                                        <div className="d-flex gap-3">
                                            <div className="w-40px">
                                                <span className="icon-circle me-3"><RupeeIcon /></span>
                                            </div>
                                            <div className="d-flex flex-column gap-2">
                                                <Label htmlFor='name' className='form-label input-label mb-0'>
                                                    {milestone?.specifications?.purchase_required ? 'Reimbursement' : 'Barter'} Budget
                                                    <span className="text-danger">*</span>
                                                </Label>
                                                <FieldInstruction instruction={FIELDS_INSTRUCTIONS.MILESTONE.REIMBURSEMENT_BUDGET} />
                                            </div>
                                        </div>
                                    </Col>
                                    <Col sm={8} className='my-1'>
                                        <InputGroup>
                                            <Input
                                                type="number"
                                                value={milestone.barter_amount}
                                                disabled={disabled}
                                                onChange={(e) => { }}
                                            />
                                        </InputGroup>
                                        {
                                            errors.barter_amount &&
                                            <span className="text-danger mt-2" id='error-barter_amount'>{errors.barter_amount}</span>
                                        }
                                    </Col>
                                </FormGroup>
                                <FormGroup row className='py-2 mt-3'> {/* Gigs */}
                                    <Col sm={4} className='my-1'>
                                        <div className="d-flex gap-3">
                                            <div className="w-40px">
                                                <span className="icon-circle me-3"><DocIcon /></span>
                                            </div>
                                            <div className="d-flex flex-column gap-2">
                                                <Label htmlFor='name' className='form-label input-label mb-0'>
                                                    {gigObjectName(milestone.gig_type_id)}s Selections
                                                    <span className="text-danger"> *</span>
                                                </Label>
                                                <FieldInstruction instruction={FIELDS_INSTRUCTIONS.MILESTONE.GIG_SELECTION.replaceAll('<GIG>', gigObjectName(milestone.gig_type_id)?.toLowerCase() ?? 'gig')} />
                                            </div>
                                        </div>
                                    </Col>
                                    <Col sm={8} className='my-1'>
                                        <div className="mb-3">
                                            <RadioGroup
                                                name="gig_selection"
                                                disabled={disabled}
                                                options={[{ label: 'Choose from list', id: 'list' }, { label: 'Find nearby', id: 'find_nearby' }]}
                                                defaultValue={milestone.specifications?.gig_selection ? 'list' : 'find_nearby'}
                                                onChange={(val) => {
                                                    // const specifications = { ...milestone.specifications, gig_selection: (val === 'list') }
                                                    // handleMilestoneChange(index, 'specifications', specifications);
                                                }}
                                            />
                                        </div>

                                        <div className="card-gray d-flex flex-column gap-3">
                                            {
                                                milestone?.specifications?.gig_selection ? (
                                                    <>
                                                        <AccessControl privilege={PRIVILEGE.CAMPAIGN.CAMPAIGN.EDIT}>
                                                            <button type="button" onClick={() => setAddGigModalOpen(true)} className="theme-btn theme-btn-primary-outlined gap-1 w-100">
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="25" height="24" viewBox="0 0 25 24" fill="none">
                                                                    <path d="M13.25 9C13.25 8.58579 12.9142 8.25 12.5 8.25C12.0858 8.25 11.75 8.58579 11.75 9L11.75 11.25H9.5C9.08579 11.25 8.75 11.5858 8.75 12C8.75 12.4142 9.08579 12.75 9.5 12.75H11.75V15C11.75 15.4142 12.0858 15.75 12.5 15.75C12.9142 15.75 13.25 15.4142 13.25 15L13.25 12.75H15.5C15.9142 12.75 16.25 12.4142 16.25 12C16.25 11.5858 15.9142 11.25 15.5 11.25H13.25V9Z" fill="#007AFF" />
                                                                    <path fillRule="evenodd" clipRule="evenodd" d="M12.5 1.25C6.56294 1.25 1.75 6.06294 1.75 12C1.75 17.9371 6.56294 22.75 12.5 22.75C18.4371 22.75 23.25 17.9371 23.25 12C23.25 6.06294 18.4371 1.25 12.5 1.25ZM3.25 12C3.25 6.89137 7.39137 2.75 12.5 2.75C17.6086 2.75 21.75 6.89137 21.75 12C21.75 17.1086 17.6086 21.25 12.5 21.25C7.39137 21.25 3.25 17.1086 3.25 12Z" fill="#007AFF" />
                                                                </svg>
                                                                <span>Add {gigObjectName(milestone.gig_type_id)}</span>
                                                            </button>
                                                        </AccessControl>

                                                        <div className="row g-3">
                                                            {
                                                                milestoneGigs(milestone).length > 0 ?
                                                                    milestoneGigs(milestone).map((gig, i) => (
                                                                        <div className="col-6" key={i}>
                                                                            <div className="gig-card h-100">
                                                                                <button
                                                                                    type="button"
                                                                                    className="w-100 text-start border-0 bg-transparent p-0"
                                                                                >
                                                                                    {
                                                                                        getGigTypeSlug(milestone.gig_type_id) === 'store_visit'
                                                                                            ? <SelectedStore gigs={[gig]} showDetails={false} showCTA={false} />
                                                                                            : <CampaignGig gig={gig} gigType={getGigTypeSlug(milestone.gig_type_id)} onPlayBtnClick={noop} showCTA={false} />
                                                                                    }
                                                                                </button>
                                                                                {
                                                                                    // (milestoneGigs(milestone).length > 1) &&
                                                                                    // <button type="button" className="unset-all mt-2" onClick={() => removeMilestoneGig(index, gig.id)}>
                                                                                    //     {/* SVG delete icon */}
                                                                                    //     <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                                    //         <path d="M10.0303 8.96965C9.73741 8.67676 9.26253 8.67676 8.96964 8.96965C8.67675 9.26255 8.67675 9.73742 8.96964 10.0303L10.9393 12L8.96966 13.9697C8.67677 14.2625 8.67677 14.7374 8.96966 15.0303C9.26255 15.3232 9.73743 15.3232 10.0303 15.0303L12 13.0607L13.9696 15.0303C14.2625 15.3232 14.7374 15.3232 15.0303 15.0303C15.3232 14.7374 15.3232 14.2625 15.0303 13.9696L13.0606 12L15.0303 10.0303C15.3232 9.73744 15.3232 9.26257 15.0303 8.96968C14.7374 8.67678 14.2625 8.67678 13.9696 8.96968L12 10.9393L10.0303 8.96965Z" fill="#007AFF" />
                                                                                    //         <path fillRule="evenodd" clipRule="evenodd" d="M12 1.25C6.06294 1.25 1.25 6.06294 1.25 12C1.25 17.9371 6.06294 22.75 12 22.75C17.9371 22.75 22.75 17.9371 22.75 12C22.75 6.06294 17.9371 1.25 12 1.25ZM2.75 12C2.75 6.89137 6.89137 2.75 12 2.75C17.1086 2.75 21.25 6.89137 21.25 12C21.25 17.1086 17.1086 21.25 12 21.25C6.89137 21.25 2.75 17.1086 2.75 12Z" fill="#007AFF" />
                                                                                    //     </svg>
                                                                                    //     <span className="visually-hidden">Remove {gigObjectName(milestone.gig_type_id)}</span>
                                                                                    // </button>
                                                                                }
                                                                            </div>
                                                                        </div>
                                                                    ))
                                                                    :
                                                                    <div className="col-12 text-muted text-center">No {gigObjectName(milestone.gig_type_id)?.toLowerCase()}s added</div>
                                                            }
                                                        </div>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Input
                                                            type="text"
                                                            disabled={disabled}
                                                            value={activeMilestone?.specifications?.store_name ?? ''}
                                                            placeholder="Store Name"
                                                            onChange={(e) => {
                                                                // const specifications = { ...milestone.specifications, store_name: e.target.value }
                                                                // handleMilestoneChange(index, 'specifications', specifications);
                                                            }}
                                                        />
                                                        <FieldInstruction instruction={FIELDS_INSTRUCTIONS.MILESTONE.STORE_NAME} />
                                                        {
                                                            errors.store_name &&
                                                            <span className="text-danger mt-2" id='error-store_name'>{errors.store_name}</span>
                                                        }
                                                    </>
                                                )
                                            }
                                        </div>
                                        {
                                            milestoneGigsUpdateStatus &&
                                            <>
                                                {
                                                    milestoneGigsUpdateStatus.status === 'success' ? (
                                                        <span className={`text-${milestoneGigsUpdateStatus.status} d-flex align-items-center gap-1`}><i className="mdi mdi-check-circle fs-4"></i>{milestoneGigsUpdateStatus.message}</span>
                                                    ) : (
                                                        <span className={`text-${milestoneGigsUpdateStatus.status} d-flex align-items-center gap-1`}> <i className="ri-close-circle-fill fs-4"></i> {milestoneGigsUpdateStatus.message}</span>
                                                    )
                                                }
                                            </>
                                        }
                                        {
                                            errors.campaign_gig_ids &&
                                            <span className="text-danger mt-2" id='error-campaign_gig_ids'>{errors.campaign_gig_ids}</span>
                                        }
                                    </Col>
                                </FormGroup>
                                {
                                    milestone?.specifications?.gig_selection &&
                                    <FormGroup row className='py-2 pb-4 mt-3'> {/* Allow influencer to choose */}
                                        <Col sm={4} className='my-1'>
                                            <div className="d-flex gap-3">
                                                <div className="w-40px">
                                                    <span className="icon-circle me-3"><DocIcon /></span>
                                                </div>
                                                <div className="d-flex flex-column gap-2">
                                                    <Label htmlFor='name' className='form-label input-label mb-0'>
                                                        {milestonesData?.properties?.gig_influencer?.label} {getGigType(milestone.gig_type_id)?.object_name}
                                                        {milestonesData?.properties?.gig_influencer?.mandatory && <span className="text-danger"> *</span>}
                                                    </Label>
                                                    <FieldInstruction instruction={`Let the influencer choose ${getGigType(milestone.gig_type_id)?.object_name?.toLowerCase()} from list`} />
                                                </div>
                                            </div>
                                        </Col>
                                        <Col sm={8} className='my-1'>
                                            <FormField
                                                showLabel={false}
                                                disabled={disabled}
                                                property={{ ...milestonesData?.properties?.gig_influencer, label: '' }}
                                                value={!!milestone.specifications.gig_influencer}
                                                onChange={(value) => {
                                                    const specifications = { ...milestone.specifications, gig_influencer: value };
                                                    handleMilestoneChange('specifications', specifications);
                                                }}
                                            />
                                        </Col>
                                    </FormGroup>
                                }
                            </>
                        ) : (
                            <>
                                <div className="divider"></div>
                                <FormGroup row className='py-2 pt-4 mt-3'> {/* Max Gigs */}
                                    <Col sm={4} className='my-1'>
                                        <div className="d-flex gap-3 align-items-center">
                                            <div className="w-40px">
                                                <span className="icon-circle me-3"><DocIcon /></span>
                                            </div>
                                            <div className="d-flex flex-column gap-2">
                                                <Label htmlFor='name' className='form-label input-label mb-0'>
                                                    No of {gigObjectName(milestone.gig_type_id)}s to be selected
                                                    <span className="text-danger"> *</span>
                                                </Label>
                                                {/* <FieldInstruction instruction={FIELDS_INSTRUCTIONS.CAMPAIGN.DESCRIPTION} /> */}
                                            </div>
                                        </div>
                                    </Col>
                                    <Col sm={8} className='my-1'>
                                        <Input
                                            type='number'
                                            disabled={disabled}
                                            value={milestone.maximum_gigs || ''}
                                            onChange={(e) => handleMilestoneChange('maximum_gigs', Number(e.target.value) ? Number(e.target.value) : '')}
                                        />
                                        {
                                            errors.maximum_gigs &&
                                            <span className="text-danger mt-2" id='error-maximum_gigs'>{errors.maximum_gigs}</span>
                                        }
                                    </Col>
                                </FormGroup>
                                <React.Fragment>
                                    {
                                        getGigTypeSlug(milestone.gig_type_id) === 'product_review' &&
                                        <FormGroup row className='py-2 mt-3'> {/* Product Delivery */}
                                            <Col sm={4} className='my-1'>
                                                <div className="d-flex gap-3">
                                                    <div className="w-40px">
                                                        <span className="icon-circle me-3"><DocIcon /></span>
                                                    </div>
                                                    <div className="d-flex flex-column gap-2">
                                                        <Label htmlFor='name' className='form-label input-label mb-0'>
                                                            Product Delivery
                                                            <span className="text-danger">*</span>
                                                        </Label>
                                                        <FieldInstruction instruction={FIELDS_INSTRUCTIONS.MILESTONE.PRODUCT_DELIVERY} />
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col sm={8} className='my-1'>
                                                <RadioGroup
                                                    name="purchase_required"
                                                    disabled={disabled}
                                                    options={[{ label: 'Sponsored Delivery', id: 'sponsored' }, { label: 'Influencer Self purchased', id: 'self_purchase' }]}
                                                    defaultValue={milestone.specifications?.purchase_required ? 'self_purchase' : 'sponsored'}
                                                    onChange={(val) => {
                                                        // const specifications = { ...milestone.specifications, purchase_required: (val === 'self_purchase') }
                                                        // handleMilestoneChange(index, 'specifications', specifications);
                                                    }}
                                                />
                                            </Col>
                                        </FormGroup>
                                    }
                                    <FormGroup row className='py-2 mt-3'> {/* Gigs */}
                                        <Col sm={4} className='my-1'>
                                            <div className="d-flex gap-3">
                                                <div className="w-40px">
                                                    <span className="icon-circle me-3"><DocIcon /></span>
                                                </div>
                                                <div className="d-flex flex-column gap-2">
                                                    <Label htmlFor='name' className='form-label input-label mb-0'>
                                                        {gigObjectName(milestone.gig_type_id)}s Selections
                                                        <span className="text-danger"> *</span>
                                                    </Label>
                                                    <FieldInstruction instruction={FIELDS_INSTRUCTIONS.MILESTONE.GIG_SELECTION.replaceAll('<GIG>', gigObjectName(milestone.gig_type_id)?.toLowerCase() ?? 'gig')} />
                                                </div>
                                            </div>
                                        </Col>
                                        <Col sm={8} className='my-1'>
                                            <div className="card-gray d-flex flex-column gap-3">
                                                <AccessControl privilege={PRIVILEGE.CAMPAIGN.CAMPAIGN.EDIT}>
                                                    <button type="button" onClick={() => setAddGigModalOpen(true)} className="theme-btn theme-btn-primary-outlined gap-1 w-100">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="25" height="24" viewBox="0 0 25 24" fill="none">
                                                            <path d="M13.25 9C13.25 8.58579 12.9142 8.25 12.5 8.25C12.0858 8.25 11.75 8.58579 11.75 9L11.75 11.25H9.5C9.08579 11.25 8.75 11.5858 8.75 12C8.75 12.4142 9.08579 12.75 9.5 12.75H11.75V15C11.75 15.4142 12.0858 15.75 12.5 15.75C12.9142 15.75 13.25 15.4142 13.25 15L13.25 12.75H15.5C15.9142 12.75 16.25 12.4142 16.25 12C16.25 11.5858 15.9142 11.25 15.5 11.25H13.25V9Z" fill="#007AFF" />
                                                            <path fillRule="evenodd" clipRule="evenodd" d="M12.5 1.25C6.56294 1.25 1.75 6.06294 1.75 12C1.75 17.9371 6.56294 22.75 12.5 22.75C18.4371 22.75 23.25 17.9371 23.25 12C23.25 6.06294 18.4371 1.25 12.5 1.25ZM3.25 12C3.25 6.89137 7.39137 2.75 12.5 2.75C17.6086 2.75 21.75 6.89137 21.75 12C21.75 17.1086 17.6086 21.25 12.5 21.25C7.39137 21.25 3.25 17.1086 3.25 12Z" fill="#007AFF" />
                                                        </svg>
                                                        <span>Add {gigObjectName(milestone.gig_type_id)}</span>
                                                    </button>
                                                </AccessControl>

                                                <div className="row g-3">
                                                    {
                                                        milestoneGigs(milestone).length > 0 ?
                                                            milestoneGigs(milestone).map((gig, i) => (
                                                                <div className="col-6" key={i}>
                                                                    <div className="gig-card h-100">
                                                                        <button
                                                                            type="button"
                                                                            className="w-100 text-start border-0 bg-transparent p-0"
                                                                        >
                                                                            {
                                                                                getGigTypeSlug(milestone.gig_type_id) === 'store_visit'
                                                                                    ? <SelectedStore gigs={[gig]} showDetails={false} showCTA={false} />
                                                                                    : <CampaignGig gig={gig} gigType={getGigTypeSlug(milestone.gig_type_id)} onPlayBtnClick={noop} showCTA={false} />
                                                                            }
                                                                        </button>
                                                                        {
                                                                            // (milestoneGigs(milestone).length > 1) &&
                                                                            // <button type="button" className="unset-all mt-2" onClick={() => removeMilestoneGig(index, gig.id)}>
                                                                            //     {/* SVG delete icon */}
                                                                            //     <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                            //         <path d="M10.0303 8.96965C9.73741 8.67676 9.26253 8.67676 8.96964 8.96965C8.67675 9.26255 8.67675 9.73742 8.96964 10.0303L10.9393 12L8.96966 13.9697C8.67677 14.2625 8.67677 14.7374 8.96966 15.0303C9.26255 15.3232 9.73743 15.3232 10.0303 15.0303L12 13.0607L13.9696 15.0303C14.2625 15.3232 14.7374 15.3232 15.0303 15.0303C15.3232 14.7374 15.3232 14.2625 15.0303 13.9696L13.0606 12L15.0303 10.0303C15.3232 9.73744 15.3232 9.26257 15.0303 8.96968C14.7374 8.67678 14.2625 8.67678 13.9696 8.96968L12 10.9393L10.0303 8.96965Z" fill="#007AFF" />
                                                                            //         <path fillRule="evenodd" clipRule="evenodd" d="M12 1.25C6.06294 1.25 1.25 6.06294 1.25 12C1.25 17.9371 6.06294 22.75 12 22.75C17.9371 22.75 22.75 17.9371 22.75 12C22.75 6.06294 17.9371 1.25 12 1.25ZM2.75 12C2.75 6.89137 6.89137 2.75 12 2.75C17.1086 2.75 21.25 6.89137 21.25 12C21.25 17.1086 17.1086 21.25 12 21.25C6.89137 21.25 2.75 17.1086 2.75 12Z" fill="#007AFF" />
                                                                            //     </svg>
                                                                            //     <span className="visually-hidden">Remove {gigObjectName(milestone.gig_type_id)}</span>
                                                                            // </button>
                                                                        }
                                                                    </div>
                                                                </div>
                                                            ))
                                                            :
                                                            <div className="col-12 text-muted text-center">No {gigObjectName(milestone.gig_type_id)?.toLowerCase()}s added</div>
                                                    }
                                                </div>
                                            </div>
                                            {
                                                milestoneGigsUpdateStatus &&
                                                <>
                                                    {
                                                        milestoneGigsUpdateStatus.status === 'success' ? (
                                                            <span className={`text-${milestoneGigsUpdateStatus.status} d-flex align-items-center gap-1`}><i className="mdi mdi-check-circle fs-4"></i>{milestoneGigsUpdateStatus.message}</span>
                                                        ) : (
                                                            <span className={`text-${milestoneGigsUpdateStatus.status} d-flex align-items-center gap-1`}> <i className="ri-close-circle-fill fs-4"></i> {milestoneGigsUpdateStatus.message}</span>
                                                        )
                                                    }
                                                </>
                                            }
                                            {
                                                errors.campaign_gig_ids &&
                                                <span className="text-danger mt-2" id='error-campaign_gig_ids'>{errors.campaign_gig_ids}</span>
                                            }
                                        </Col>
                                    </FormGroup>
                                    {
                                        getGigTypeSlug(milestone?.gig_type_id) === 'product_review' &&
                                        <>
                                            <FormGroup row className='py-2 mt-3'> {/* Reimbursement Budget */}
                                                <Col sm={4} className='my-1'>
                                                    <div className="d-flex gap-3">
                                                        <div className="w-40px">
                                                            <span className="icon-circle me-3"><RupeeIcon /></span>
                                                        </div>
                                                        <div className="d-flex flex-column gap-2">
                                                            <Label htmlFor='name' className='form-label input-label mb-0'>
                                                                {milestone?.specifications?.purchase_required ? 'Reimbursement' : 'Barter'} Budget
                                                            </Label>
                                                            <FieldInstruction instruction={FIELDS_INSTRUCTIONS.MILESTONE.REIMBURSEMENT_BUDGET} />
                                                        </div>
                                                    </div>
                                                </Col>
                                                <Col sm={8} className='my-1'>
                                                    <InputGroup>
                                                        <Input
                                                            disabled={disabled}
                                                            type="number"
                                                            value={milestone.barter_amount}
                                                            onChange={(e) => handleMilestoneChange('barter_amount', Number(e.target.value) ? Number(e.target.value) : '')}
                                                        />
                                                    </InputGroup>
                                                    {
                                                        errors.barter_amount &&
                                                        <span className="text-danger mt-2" id='error-barter_amount'>{errors.barter_amount}</span>
                                                    }
                                                </Col>
                                            </FormGroup>
                                        </>
                                    }
                                    <FormGroup row className='py-2 pb-4 mt-3'> {/* Allow influencer to choose */}
                                        <Col sm={4} className='my-1'>
                                            <div className="d-flex gap-3">
                                                <div className="w-40px">
                                                    <span className="icon-circle me-3"><DocIcon /></span>
                                                </div>
                                                <div className="d-flex flex-column gap-2">
                                                    <Label htmlFor='name' className='form-label input-label mb-0'>
                                                        {milestonesData?.properties?.gig_influencer?.label} {getGigType(milestone.gig_type_id)?.object_name}
                                                        {milestonesData?.properties?.gig_influencer?.mandatory && <span className="text-danger"> *</span>}
                                                    </Label>
                                                    <FieldInstruction instruction={`Let the influencer choose ${getGigType(milestone.gig_type_id)?.object_name?.toLowerCase()} from list`} />
                                                </div>
                                            </div>
                                        </Col>
                                        <Col sm={8} className='my-1'>
                                            <FormField
                                                showLabel={false}
                                                disabled={disabled}
                                                property={{ ...milestonesData?.properties?.gig_influencer, label: '' }}
                                                value={!!milestone.specifications.gig_influencer}
                                                onChange={(value) => {
                                                    const specifications = { ...milestone.specifications, gig_influencer: value };
                                                    handleMilestoneChange('specifications', specifications);
                                                }}
                                            />
                                        </Col>
                                    </FormGroup>
                                </React.Fragment>
                            </>
                        )
                    }
                </>
            }
            {
                Array.isArray(milestone?.specifications?.cta) && milestone.specifications.cta.filter((cta) => !!cta.text).length > 0 &&
                <>
                    <div className="divider"></div>

                    <FormGroup row className='py-2 pt-4 mt-3'> {/* CTAs */}
                        <Col sm={4} className='my-1'>
                            <div className="d-flex gap-3">
                                <div className="w-40px">
                                    <span className="icon-circle me-3"><DocIcon /></span>
                                </div>
                                <div className="d-flex flex-column gap-2">
                                    <Label htmlFor='name' className='form-label input-label mb-0'>
                                        {milestonesData?.properties?.cta?.label}
                                    </Label>
                                    <FieldInstruction instruction={FIELDS_INSTRUCTIONS.MILESTONE.CTA} />
                                </div>
                            </div>
                        </Col>
                        <Col sm={8} className='my-1'>
                            <div className="card-gray d-flex flex-column gap-3">
                                <AccessControl privilege={PRIVILEGE.CAMPAIGN.CAMPAIGN.EDIT}>
                                    <button type="button" className="theme-btn theme-btn-primary-outlined gap-1 w-100" onClick={() => setAddCTAModalOpen(true)}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="25" height="24" viewBox="0 0 25 24" fill="none">
                                            <path d="M13.25 9C13.25 8.58579 12.9142 8.25 12.5 8.25C12.0858 8.25 11.75 8.58579 11.75 9L11.75 11.25H9.5C9.08579 11.25 8.75 11.5858 8.75 12C8.75 12.4142 9.08579 12.75 9.5 12.75H11.75V15C11.75 15.4142 12.0858 15.75 12.5 15.75C12.9142 15.75 13.25 15.4142 13.25 15L13.25 12.75H15.5C15.9142 12.75 16.25 12.4142 16.25 12C16.25 11.5858 15.9142 11.25 15.5 11.25H13.25V9Z" fill="#007AFF" />
                                            <path fillRule="evenodd" clipRule="evenodd" d="M12.5 1.25C6.56294 1.25 1.75 6.06294 1.75 12C1.75 17.9371 6.56294 22.75 12.5 22.75C18.4371 22.75 23.25 17.9371 23.25 12C23.25 6.06294 18.4371 1.25 12.5 1.25ZM3.25 12C3.25 6.89137 7.39137 2.75 12.5 2.75C17.6086 2.75 21.75 6.89137 21.75 12C21.75 17.1086 17.6086 21.25 12.5 21.25C7.39137 21.25 3.25 17.1086 3.25 12Z" fill="#007AFF" />
                                        </svg>
                                        <span>Add CTA</span>
                                    </button>
                                </AccessControl>
                                <div className="row g-3">
                                    {
                                        milestone?.specifications?.cta?.length > 0 ?
                                            milestone?.specifications?.cta?.map((cta: any, i: number) => (
                                                <div className="col-6" key={i}>
                                                    <div className="gig-card cursor-pointer h-100">
                                                        <div
                                                            className="w-100 d-flex flex-column text-start border-0 bg-transparent p-0"
                                                        >
                                                            <span> {cta.text}</span>
                                                            <span className="text-muted" style={{ fontSize: '12px' }}>{cta.url}</span>
                                                        </div>

                                                        {/*<button type="button" className="unset-all mt-2" onClick={(e) => {
                                                            e.stopPropagation();
                                                            removeCTA(index, i)
                                                        }}>
                                                            {/* SVG delete icon */}
                                                        {/*  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <path d="M10.0303 8.96965C9.73741 8.67676 9.26253 8.67676 8.96964 8.96965C8.67675 9.26255 8.67675 9.73742 8.96964 10.0303L10.9393 12L8.96966 13.9697C8.67677 14.2625 8.67677 14.7374 8.96966 15.0303C9.26255 15.3232 9.73743 15.3232 10.0303 15.0303L12 13.0607L13.9696 15.0303C14.2625 15.3232 14.7374 15.3232 15.0303 15.0303C15.3232 14.7374 15.3232 14.2625 15.0303 13.9696L13.0606 12L15.0303 10.0303C15.3232 9.73744 15.3232 9.26257 15.0303 8.96968C14.7374 8.67678 14.2625 8.67678 13.9696 8.96968L12 10.9393L10.0303 8.96965Z" fill="#007AFF" />
                                                                <path fillRule="evenodd" clipRule="evenodd" d="M12 1.25C6.06294 1.25 1.25 6.06294 1.25 12C1.25 17.9371 6.06294 22.75 12 22.75C17.9371 22.75 22.75 17.9371 22.75 12C22.75 6.06294 17.9371 1.25 12 1.25ZM2.75 12C2.75 6.89137 6.89137 2.75 12 2.75C17.1086 2.75 21.25 6.89137 21.25 12C21.25 17.1086 17.1086 21.25 12 21.25C6.89137 21.25 2.75 17.1086 2.75 12Z" fill="#007AFF" />
                                                            </svg>
                                                            <span className="visually-hidden">Remove CTA</span>
                                                        </button>
                                                        */}
                                                    </div>
                                                </div>
                                            ))
                                            :
                                            <div className="col-12 text-muted text-center">No CTAs added</div>
                                    }
                                </div>
                            </div>
                            {
                                errors.cta &&
                                <span className="text-danger mt-2" id='error-cta'>{errors.cta}</span>
                            }
                        </Col>
                    </FormGroup>
                    <FormGroup row className='py-2 pb-4 mt-3'> {/* Allow Influencer to choose CTA */}
                        <Col sm={4} className='my-1'>
                            <div className="d-flex gap-3 align-items-center">
                                <div className="w-40px">
                                    <span className="icon-circle me-3"><DocIcon /></span>
                                </div>
                                <div className="d-flex flex-column gap-2">
                                    <Label htmlFor='name' className='form-label input-label mb-0'>
                                        {milestonesData?.properties?.cta_influencer?.label}
                                    </Label>
                                    {/* <FieldInstruction instruction={FIELDS_INSTRUCTIONS.MILESTONE.SCRIPT_SUBMISSION} /> */}
                                </div>
                            </div>
                        </Col>
                        <Col sm={8} className='my-1'>
                            <FormField
                                property={{ ...milestonesData?.properties?.cta_influencer, label: '' }}
                                value={!!milestone.specifications.cta_influencer}
                                showLabel={false}
                                disabled={disabled}
                                onChange={(value) => {
                                    const specifications = { ...milestone.specifications, cta_influencer: value };
                                    handleMilestoneChange('specifications', specifications);
                                }}
                            />
                        </Col>
                    </FormGroup>
                    {
                        canSetDMAutomation &&
                        <>
                            <FormGroup row className={`py-2  ${milestone.specifications?.dm_automation_required ? 'pb-2' : 'pb-4'} mt-3`}> {/* DM Automation Required */}
                                <Col sm={4} className='my-1'>
                                    <div className="d-flex gap-3 align-items-center">
                                        <div className="w-40px">
                                            <span className="icon-circle me-3"><DocIcon /></span>
                                        </div>
                                        <div className="d-flex flex-column gap-2">
                                            <Label htmlFor='name' className='form-label input-label mb-0'>
                                                {milestonesData?.properties?.dm_automation_required?.label}
                                            </Label>
                                            {/* <FieldInstruction instruction={FIELDS_INSTRUCTIONS.MILESTONE.SCRIPT_SUBMISSION} /> */}
                                        </div>
                                    </div>
                                </Col>
                                <Col sm={8} className='my-1 d-flex align-items-center'>
                                    <FormField
                                        property={{ ...milestonesData?.properties?.dm_automation_required, label: '' }}
                                        value={!!milestone.specifications.dm_automation_required}
                                        showLabel={false}
                                        disabled={disabled}
                                        onChange={(value) => {
                                            const specifications = { ...milestone.specifications, dm_automation_required: value };
                                            // handleMilestoneChange(index, 'specifications', specifications);
                                        }}
                                    />
                                </Col>
                            </FormGroup>
                            {
                                milestone.specifications?.dm_automation_required &&
                                <>
                                    <FormGroup row className='py-2 pb-2 mt-3'> {/* DM Automation Triggers */}
                                        <Col sm={4} className='my-1'>
                                            <div className="d-flex gap-3 align-items-center">
                                                <div className="w-40px">
                                                    <span className="icon-circle me-3"><DocIcon /></span>
                                                </div>
                                                <div className="d-flex flex-column gap-2">
                                                    <Label htmlFor='name' className='form-label input-label mb-0'>
                                                        {milestonesData?.properties?.dm_automation_triggers?.label}
                                                        <span className="text-danger"> *</span>
                                                    </Label>
                                                    {/* <FieldInstruction instruction={FIELDS_INSTRUCTIONS.MILESTONE.SCRIPT_SUBMISSION} /> */}
                                                </div>
                                            </div>
                                        </Col>
                                        <Col sm={8} className='my-1'>
                                            <CustomMultiSelect
                                                disabled={disabled}
                                                placeholder="Select DM Automation Triggers"
                                                options={milestonesData?.dm_automation_trigger_list?.map((trigger: string) => ({ label: trigger, value: trigger })) || []}
                                                values={(milestone.specifications?.dm_automation_triggers ?? [])}
                                                onChange={(triggers) => {
                                                    const specifications = { ...milestone.specifications, dm_automation_triggers: triggers };
                                                    // handleMilestoneChange(index, 'specifications', specifications);
                                                }}
                                            />
                                            {
                                                errors.dm_automation_triggers &&
                                                <span className="text-danger mt-2" id='error-dm_automation_triggers'>{errors.dm_automation_triggers}</span>
                                            }
                                        </Col>
                                    </FormGroup>
                                    <FormGroup row className='py-2 pb-4 mt-3'> {/* DM Automation Button Text */}
                                        <Col sm={4} className='my-1'>
                                            <div className="d-flex gap-3 align-items-center">
                                                <div className="w-40px">
                                                    <span className="icon-circle me-3"><DocIcon /></span>
                                                </div>
                                                <div className="d-flex flex-column gap-2">
                                                    <Label htmlFor='name' className='form-label input-label mb-0'>
                                                        {milestonesData?.properties?.dm_automation_button_text?.label}
                                                        <span className="text-danger"> *</span>
                                                    </Label>
                                                    {/* <FieldInstruction instruction={FIELDS_INSTRUCTIONS.MILESTONE.SCRIPT_SUBMISSION} /> */}
                                                </div>
                                            </div>
                                        </Col>
                                        <Col sm={8} className='my-1'>
                                            <Select
                                                isDisabled={disabled}
                                                options={milestonesData?.dm_automation_button_text_list?.map((text: string) => ({ label: text, value: text })) || []}
                                                value={{ label: milestone.specifications?.dm_automation_button_text ?? "", value: milestone.specifications?.dm_automation_button_text ?? "" }}
                                                onChange={(option: any) => {
                                                    const specifications = { ...milestone.specifications, dm_automation_button_text: option?.value ?? "" };
                                                    // handleMilestoneChange(index, 'specifications', specifications);
                                                }}
                                                isClearable
                                                placeholder="Select DM Automation Button Text"
                                            />
                                            {
                                                errors.dm_automation_button_text &&
                                                <span className="text-danger mt-2" id='error-dm_automation_button_text'>{errors.dm_automation_button_text}</span>
                                            }
                                        </Col>
                                    </FormGroup>
                                </>
                            }
                        </>
                    }
                </>
            }
            {
                milestonesData && milestonesData.properties &&
                Object.values(milestonesData.properties).filter((property) => (property.gig_type_id === milestone.gig_type_id && property.key !== 'purchase_required')).length > 0 &&
                <div className="divider"></div>
            }
            {
                milestonesData && milestonesData.properties &&
                Object.values(milestonesData.properties).map((property, i) =>
                    (property.gig_type_id === milestone.gig_type_id && property.auto_render) ? (
                        <React.Fragment key={i}>
                            {
                                (property.key === 'review_snapshot_platform' && !milestone.specifications?.review_snapshot_required) ? null : (
                                    <FormGroup row className={`py-4 mt-3`}> {/* Properties Loop */}
                                        <Col sm={4} className='my-1'>
                                            <div className="d-flex gap-3 align-items-center">
                                                <div className="w-40px">
                                                    <span className="icon-circle me-3"><DocIcon /></span>
                                                </div>
                                                <div className="d-flex flex-column gap-2">
                                                    <Label htmlFor='name' className='form-label input-label mb-0'>
                                                        {property.label}
                                                        {
                                                            property.mandatory && <span className="text-danger"> *</span>
                                                        }
                                                    </Label>
                                                    {/* <FieldInstruction instruction={FIELDS_INSTRUCTIONS.CAMPAIGN.DESCRIPTION} /> */}
                                                </div>
                                            </div>
                                        </Col>
                                        <Col sm={8} className='my-1'>
                                            <FormField
                                                showLabel={false}
                                                property={property}
                                                value={milestone.specifications[property.key]}
                                                disabled={disabled}
                                                onChange={(value) => {
                                                    const specifications = { ...milestone.specifications, [property.key]: value };
                                                    handleMilestoneChange('specifications', specifications);
                                                }}
                                            />
                                        </Col>
                                    </FormGroup>
                                )
                            }

                        </React.Fragment>
                    ) : null
                )
            }
            <div className="divider"></div>
            {
                Array.isArray(milestone?.specifications?.talking_points) && milestone.specifications.talking_points.filter((tp) => tp.title && tp.pointers).length > 0 &&
                <FormGroup row className='py-4 mt-3'> {/* Talking Points */}
                    <Col sm={4} className='my-1'>
                        <div className="d-flex gap-3 align-items-center">
                            <div className="w-40px">
                                <span className="icon-circle me-3"><DocIcon /></span>
                            </div>
                            <div className="d-flex flex-column gap-2">
                                <Label htmlFor='name' className='form-label input-label mb-0'>
                                    {milestonesData?.properties?.talking_points?.label || 'Talking Points'}
                                </Label>
                                {/* <FieldInstruction instruction={FIELDS_INSTRUCTIONS.CAMPAIGN.DESCRIPTION} /> */}
                            </div>
                        </div>
                    </Col>
                    <Col sm={8} className='my-1'>
                        <TalkingPoints talkingPoints={milestone.specifications?.talking_points ?? []} onChange={(newTalkingPoints) => {
                            const specifications = { ...milestone.specifications, talking_points: newTalkingPoints }
                            handleMilestoneChange('specifications', specifications);
                        }} disabled={disabled} />
                    </Col>
                </FormGroup>
            }

            {
                getGigTypeSlug(milestone?.gig_type_id) !== 'repost' &&
                <>
                    <div className="divider"></div>
                    {
                        Array.isArray(milestone.specifications?.sample_scripts) && milestone.specifications.sample_scripts.length > 0 &&
                        <FormGroup row className='py-2 pt-4 mt-3'> {/* Sample Scripts */}
                            <Col sm={4} className='my-1'>
                                <div className="d-flex gap-3">
                                    <div className="w-40px">
                                        <span className="icon-circle me-3"><DocIcon /></span>
                                    </div>
                                    <div className="d-flex flex-column gap-2">
                                        <Label htmlFor='name' className='form-label input-label mb-0'>
                                            {milestonesData?.properties?.sample_scripts?.label}
                                        </Label>
                                        <FieldInstruction instruction={FIELDS_INSTRUCTIONS.MILESTONE.SCRIPTS} />
                                    </div>
                                </div>
                            </Col>
                            <Col sm={8} className='my-1'>
                                <RadioGroup
                                    disabled={disabled}
                                    name="allow-sample-scripts"
                                    options={[{ label: 'Yes', id: 'y' }, { label: 'No', id: 'n' }]}
                                    defaultValue={sampleScriptsAllowed ? 'y' : 'n'}
                                    onChange={(val) => {
                                        allowSampleScripts(val === 'y')
                                        // if (val === 'n') {
                                        //     handleMilestoneChange(index, 'specifications', { ...milestone.specifications, sample_scripts: [] });
                                        // }
                                    }}
                                />
                                {
                                    sampleScriptsAllowed &&
                                    <div className="d-flex flex-column gap-3 mt-3">
                                        {
                                            (milestone?.specifications?.sample_scripts ?? []).map((script: string, i: number) => (
                                                <MilestoneScript
                                                    key={i}
                                                    script={script}
                                                    disabled={disabled}
                                                    onChange={(newScript) => {
                                                        const scripts = [...(milestone.specifications?.sample_scripts || [])];
                                                        scripts[i] = newScript;
                                                        handleMilestoneChange('specifications', { ...milestone.specifications, sample_scripts: scripts });
                                                    }}
                                                    onDelete={() => {
                                                        const scripts = (milestone.specifications?.sample_scripts || []).filter((_: string, idx: number) => idx !== i);
                                                        handleMilestoneChange('specifications', { ...milestone.specifications, sample_scripts: scripts });
                                                    }}
                                                    scriptName={`Script ${i + 1}`}
                                                />
                                            ))

                                        }
                                        {/* <button type="button" className="unset-all add-more-btn" onClick={() => {
                                                    const scripts = [...(milestone.specifications?.sample_scripts || [])];
                                                    scripts.push('');
                                                    handleMilestoneChange(index, 'specifications', { ...milestone.specifications, sample_scripts: scripts });

                                                }}>
                                                    <span>
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                            <path d="M10.6243 7.50033C10.6243 7.15515 10.3445 6.87533 9.99935 6.87533C9.65417 6.87533 9.37435 7.15515 9.37435 7.50033L9.37435 9.37535H7.49935C7.15417 9.37535 6.87435 9.65517 6.87435 10.0003C6.87435 10.3455 7.15417 10.6253 7.49935 10.6253H9.37435V12.5003C9.37435 12.8455 9.65417 13.1253 9.99935 13.1253C10.3445 13.1253 10.6243 12.8455 10.6243 12.5003L10.6243 10.6253H12.4993C12.8445 10.6253 13.1243 10.3455 13.1243 10.0003C13.1243 9.65517 12.8445 9.37535 12.4993 9.37535H10.6243V7.50033Z" fill="#007AFF" />
                                                            <path fill-rule="evenodd" clip-rule="evenodd" d="M9.99935 1.04199C5.0518 1.04199 1.04102 5.05277 1.04102 10.0003C1.04102 14.9479 5.0518 18.9587 9.99935 18.9587C14.9469 18.9587 18.9577 14.9479 18.9577 10.0003C18.9577 5.05277 14.9469 1.04199 9.99935 1.04199ZM2.29102 10.0003C2.29102 5.74313 5.74215 2.29199 9.99935 2.29199C14.2565 2.29199 17.7077 5.74313 17.7077 10.0003C17.7077 14.2575 14.2565 17.7087 9.99935 17.7087C5.74215 17.7087 2.29102 14.2575 2.29102 10.0003Z" fill="#007AFF" />
                                                        </svg>
                                                    </span>
                                                    Add More Scripts
                                                </button> */}
                                    </div>
                                }
                            </Col>
                        </FormGroup>
                    }
                    <FormGroup row className='py-2 pb-4 mt-3'> {/* Allow Script Submission */}
                        <Col sm={4} className='my-1'>
                            <div className="d-flex gap-3">
                                <div className="w-40px">
                                    <span className="icon-circle me-3"><DocIcon /></span>
                                </div>
                                <div className="d-flex flex-column gap-2">
                                    <Label htmlFor='name' className='form-label input-label mb-0'>
                                        {milestonesData?.properties?.script_submission?.label}
                                    </Label>
                                    <FieldInstruction instruction={FIELDS_INSTRUCTIONS.MILESTONE.SCRIPT_SUBMISSION} />
                                </div>
                            </div>
                        </Col>
                        <Col sm={8} className='my-1'>
                            <FormField
                                property={{ ...milestonesData?.properties?.script_submission, label: '' }}
                                value={!!milestone.specifications.script_submission}
                                showLabel={false}
                                disabled={disabled}
                                onChange={(value) => {
                                    const specifications = { ...milestone.specifications, script_submission: value };
                                    handleMilestoneChange('specifications', specifications);
                                }}
                            />
                        </Col>
                    </FormGroup>
                </>
            }
            <div className="divider"></div>
            {
                getGigTypeSlug(milestone?.gig_type_id) === 'repost' &&
                <>
                    {
                        Array.isArray(milestone.specifications?.sample_captions) && milestone.specifications.sample_captions.length > 0 &&
                        <FormGroup row className='py-2 pt-4 mt-3'> {/* Sample Captions */}
                            <Col sm={4} className='my-1'>
                                <div className="d-flex gap-3">
                                    <div className="w-40px">
                                        <span className="icon-circle me-3"><DocIcon /></span>
                                    </div>
                                    <div className="d-flex flex-column gap-2">
                                        <Label htmlFor='name' className='form-label input-label mb-0'>
                                            {milestonesData?.properties?.sample_captions?.label}
                                        </Label>
                                        <FieldInstruction instruction={FIELDS_INSTRUCTIONS.MILESTONE.CAPTIONS} />
                                    </div>
                                </div>
                            </Col>
                            <Col sm={8} className='my-1'>
                                <RadioGroup
                                    disabled={disabled}
                                    name="allow-sample-captions"
                                    options={[{ label: 'Yes', id: 'y' }, { label: 'No', id: 'n' }]}
                                    defaultValue={sampleCaptionsAllowed ? 'y' : 'n'}
                                    onChange={(val) => {
                                        allowSampleCaptions(val === 'y')
                                        // if (val === 'n') {
                                        //     handleMilestoneChange(index, 'specifications', { ...milestone.specifications, sample_captions: [] });
                                        // }
                                    }}
                                />
                                {
                                    sampleCaptionsAllowed &&
                                    <div className="d-flex flex-column gap-3 mt-3">
                                        {
                                            (milestone?.specifications?.sample_captions ?? []).map((caption: string, i: number) => (
                                                <MilestoneCaption
                                                    key={i}
                                                    caption={caption}
                                                    disabled={disabled}
                                                    onChange={(newScript) => {
                                                        const captions = [...(milestone.specifications?.sample_captions || [])];
                                                        captions[i] = newScript;
                                                        handleMilestoneChange('specifications', { ...milestone.specifications, sample_captions: captions });
                                                    }}
                                                    onDelete={() => {
                                                        const captions = (milestone.specifications?.sample_captions || []).filter((_: string, idx: number) => idx !== i);
                                                        handleMilestoneChange('specifications', { ...milestone.specifications, sample_captions: captions });
                                                    }}
                                                    captionName={`Caption ${i + 1}`}
                                                />
                                            ))

                                        }
                                        {/* <button type="button" className="unset-all add-more-btn" onClick={() => {
                                                    const captions = [...(milestone.specifications?.sample_captions || [])];
                                                    captions.push('');
                                                    handleMilestoneChange(index, 'specifications', { ...milestone.specifications, sample_captions: captions });

                                                }}>
                                                    <span>
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                            <path d="M10.6243 7.50033C10.6243 7.15515 10.3445 6.87533 9.99935 6.87533C9.65417 6.87533 9.37435 7.15515 9.37435 7.50033L9.37435 9.37535H7.49935C7.15417 9.37535 6.87435 9.65517 6.87435 10.0003C6.87435 10.3455 7.15417 10.6253 7.49935 10.6253H9.37435V12.5003C9.37435 12.8455 9.65417 13.1253 9.99935 13.1253C10.3445 13.1253 10.6243 12.8455 10.6243 12.5003L10.6243 10.6253H12.4993C12.8445 10.6253 13.1243 10.3455 13.1243 10.0003C13.1243 9.65517 12.8445 9.37535 12.4993 9.37535H10.6243V7.50033Z" fill="#007AFF" />
                                                            <path fill-rule="evenodd" clip-rule="evenodd" d="M9.99935 1.04199C5.0518 1.04199 1.04102 5.05277 1.04102 10.0003C1.04102 14.9479 5.0518 18.9587 9.99935 18.9587C14.9469 18.9587 18.9577 14.9479 18.9577 10.0003C18.9577 5.05277 14.9469 1.04199 9.99935 1.04199ZM2.29102 10.0003C2.29102 5.74313 5.74215 2.29199 9.99935 2.29199C14.2565 2.29199 17.7077 5.74313 17.7077 10.0003C17.7077 14.2575 14.2565 17.7087 9.99935 17.7087C5.74215 17.7087 2.29102 14.2575 2.29102 10.0003Z" fill="#007AFF" />
                                                        </svg>
                                                    </span>
                                                    Add More Captions
                                                </button> */}
                                    </div>
                                }
                            </Col>
                        </FormGroup>
                    }
                </>
            }
            <FormGroup row className='py-2 pb-4 mt-3'> {/* Allow Caption Submission */}
                <Col sm={4} className='my-1'>
                    <div className="d-flex gap-3">
                        <div className="w-40px">
                            <span className="icon-circle me-3"><DocIcon /></span>
                        </div>
                        <div className="d-flex flex-column gap-2">
                            <Label htmlFor='name' className='form-label input-label mb-0'>
                                {milestonesData?.properties?.caption_submission?.label}
                            </Label>
                            <FieldInstruction instruction={FIELDS_INSTRUCTIONS.MILESTONE.CAPTION_SUBMISSION} />
                        </div>
                    </div>
                </Col>
                <Col sm={8} className='my-1'>
                    <FormField
                        disabled={disabled}
                        property={{ ...milestonesData?.properties?.caption_submission, label: '' }}
                        value={!!milestone.specifications.caption_submission}
                        showLabel={false}
                        onChange={(value) => {
                            const specifications = { ...milestone.specifications, caption_submission: value };
                            handleMilestoneChange('specifications', specifications);
                        }}
                    />
                </Col>
            </FormGroup>
            {
                Array.isArray(milestone.specifications?.do) && milestone.specifications.do.length > 0 &&
                <>
                    <div className="divider"></div>
                    <FormGroup row className='py-4 mt-3'> {/* Dos */}
                        <Col sm={4} className='my-1'>
                            <div className="d-flex gap-3 align-items-center">
                                <div className="w-40px">
                                    <span className="icon-circle me-3"><DocIcon /></span>
                                </div>
                                <div className="d-flex flex-column gap-2">
                                    <Label htmlFor='name' className='form-label input-label mb-0'>
                                        {milestonesData?.properties?.do?.label}
                                    </Label>
                                    {/* <FieldInstruction instruction={FIELDS_INSTRUCTIONS.MILESTONE.SCRIPTS} /> */}
                                </div>
                            </div>
                        </Col>
                        <Col sm={8} className='my-1'>
                            <ListEditor
                                disabled={disabled}
                                value={milestone?.specifications?.do ?? []}
                                onChange={(dos: string[]) => {
                                    handleMilestoneChange('specifications', { ...milestone?.specifications, do: dos })
                                }}
                                title={milestonesData?.properties?.do?.label}
                            />
                        </Col>
                    </FormGroup>
                </>
            }
            {
                Array.isArray(milestone.specifications?.dont) && milestone.specifications.dont.length > 0 &&
                <>
                    <div className="divider"></div>
                    <FormGroup row className='py-4 mt-3'> {/* Donts */}
                        <Col sm={4} className='my-1'>
                            <div className="d-flex gap-3 align-items-center">
                                <div className="w-40px">
                                    <span className="icon-circle me-3"><DocIcon /></span>
                                </div>
                                <div className="d-flex flex-column gap-2">
                                    <Label htmlFor='name' className='form-label input-label mb-0'>
                                        {milestonesData?.properties?.dont?.label}
                                    </Label>
                                    {/* <FieldInstruction instruction={FIELDS_INSTRUCTIONS.MILESTONE.SCRIPTS} /> */}
                                </div>
                            </div>
                        </Col>
                        <Col sm={8} className='my-1'>
                            <ListEditor
                                disabled={disabled}
                                value={milestone?.specifications?.dont ?? []}
                                onChange={(dont: string[]) => {
                                    handleMilestoneChange('specifications', { ...milestone?.specifications, dont })
                                }}
                                title={milestonesData?.properties?.dont?.label}
                            />
                        </Col>
                    </FormGroup>
                </>
            }
             {
                Array.isArray(milestone.specifications?.must_have) && milestone.specifications.must_have.length > 0 &&
                <>
                    <div className="divider"></div>
                    <FormGroup row className='py-4 mt-3'> {/* must_haves */}
                        <Col sm={4} className='my-1'>
                            <div className="d-flex gap-3 align-items-center">
                                <div className="w-40px">
                                    <span className="icon-circle me-3"><DocIcon /></span>
                                </div>
                                <div className="d-flex flex-column gap-2">
                                    <Label htmlFor='name' className='form-label input-label mb-0'>
                                        {milestonesData?.properties?.must_have?.label}
                                    </Label>
                                    {/* <FieldInstruction instruction={FIELDS_INSTRUCTIONS.MILESTONE.SCRIPTS} /> */}
                                </div>
                            </div>
                        </Col>
                        <Col sm={8} className='my-1'>
                            <ListEditor
                                disabled={disabled}
                                value={milestone?.specifications?.must_have ?? []}
                                onChange={(must_have: string[]) => {
                                    handleMilestoneChange('specifications', { ...milestone?.specifications, must_have })
                                }}
                                title={milestonesData?.properties?.must_have?.label}
                            />
                        </Col>
                    </FormGroup>
                </>
            }
            {
                Array.isArray(milestone.specifications?.hashtags) && milestone.specifications.hashtags.length > 0 &&
                <>
                    <div className="divider"></div>
                    <FormGroup row className='py-4 mt-3'> {/* Hashtags */}
                        <Col sm={4} className='my-1'>
                            <div className="d-flex gap-3 align-items-center">
                                <div className="w-40px">
                                    <span className="icon-circle me-3"><DocIcon /></span>
                                </div>
                                <div className="d-flex flex-column gap-2">
                                    <Label htmlFor='name' className='form-label input-label mb-0'>
                                        {milestonesData?.properties?.hashtags?.label}
                                    </Label>
                                    {/* <FieldInstruction instruction={FIELDS_INSTRUCTIONS.MILESTONE.SCRIPT_SUBMISSION} /> */}
                                </div>
                            </div>
                        </Col>
                        <Col sm={8} className='my-1'>
                            <CreatableTags
                                disabled={disabled}
                                placeholder="Add hashtags"
                                tagType='hashtag'
                                values={milestone?.specifications?.hashtags ?? []}
                                onChange={(tags) => {
                                    const specifications = { ...milestone?.specifications, hashtags: tags };
                                    handleMilestoneChange('specifications', specifications);
                                }}
                            />
                        </Col>
                    </FormGroup>
                </>
            }
            {
                Array.isArray(milestone.specifications?.mentions) && milestone.specifications.mentions.length > 0 &&
                <>
                    <div className="divider"></div>
                    <FormGroup row className='py-4 mt-3'> {/* Mentions */}
                        <Col sm={4} className='my-1'>
                            <div className="d-flex gap-3 align-items-center">
                                <div className="w-40px">
                                    <span className="icon-circle me-3"><RupeeIcon /></span>
                                </div>
                                <div className="d-flex flex-column gap-2">
                                    <Label htmlFor='name' className='form-label input-label mb-0'>
                                        {milestonesData?.properties?.mentions?.label}
                                    </Label>
                                    {/* <FieldInstruction instruction={FIELDS_INSTRUCTIONS.MILESTONE.SCRIPT_SUBMISSION} /> */}
                                </div>
                            </div>
                        </Col>
                        <Col sm={8} className='my-1'>
                            <CreatableTags
                                disabled={disabled}
                                tagType='usertag'
                                placeholder="Add mentions"
                                values={milestone?.specifications?.mentions ?? []}
                                onChange={(tags) => {
                                    const specifications = { ...milestone?.specifications, mentions: tags };
                                    handleMilestoneChange('specifications', specifications);
                                }}
                            />
                        </Col>
                    </FormGroup>
                </>
            }
            {
                Array.isArray(milestone.specifications?.collaborators) && milestone.specifications.collaborators.length > 0 &&
                <>
                    <div className="divider"></div>
                    <FormGroup row className='py-4 mt-3'> {/* Collaborators */}
                        <Col sm={4} className='my-1'>
                            <div className="d-flex gap-3 align-items-center">
                                <div className="w-40px">
                                    <span className="icon-circle me-3"><RupeeIcon /></span>
                                </div>
                                <div className="d-flex flex-column gap-2">
                                    <Label htmlFor='name' className='form-label input-label mb-0'>
                                        {milestonesData?.properties?.collaborators?.label}
                                    </Label>
                                    {/* <FieldInstruction instruction={FIELDS_INSTRUCTIONS.MILESTONE.SCRIPT_SUBMISSION} /> */}
                                </div>
                            </div>
                        </Col>
                        <Col sm={8} className='my-1'>
                            <CreatableTags
                                disabled={disabled}
                                placeholder="Add collaborators"
                                tagType='usertag'
                                values={milestone?.specifications?.collaborators ?? []}
                                onChange={(tags) => {
                                    const specifications = { ...milestone?.specifications, collaborators: tags };
                                    handleMilestoneChange('specifications', specifications);
                                }}
                            />
                        </Col>
                    </FormGroup>
                </>
            }
            {
                getCreatives().length > 0 &&
                <FormGroup row className='py-4 mt-3'> {/* Creatives */}
                    <Col sm={4} className='my-1'>
                        <div className="d-flex gap-3">
                            <div className="w-40px">
                                <span className="icon-circle me-3"><GalleryIcon /></span>
                            </div>
                            <div className="d-flex flex-column gap-2">
                                <Label htmlFor='name' className='form-label input-label mb-0'>
                                    Creatives
                                </Label>
                                <FieldInstruction instruction={FIELDS_INSTRUCTIONS.MILESTONE.CREATIVES} />
                            </div>
                        </div>
                    </Col>
                    <Col sm={8} className='my-1'>
                        <div className="gallery-container">
                            {/* <div
                                        className="campaign-creative-img-placeholder"
                                    >
                                        <button type='button' className="creative-add-btn">+</button>
                                    </div> */}

                            {getCreatives().length > 0 &&
                                getCreatives().map((image) => {
                                    if (getMediaTypeFromURL(image.url) === 'image') {
                                        return (
                                            <div key={image.uuid} className="campaign-creative-img" onClick={(e) => {
                                                e.stopPropagation()
                                                setSelectedMedia(image);
                                            }} style={{ backgroundImage: `url(${image.url})` }}>
                                                {/* <button type='button' className="creative-delete-btn" onClick={(e) => {
                                                            e.stopPropagation()
                                                            deleteMilestoneFile(image.uuid)
                                                        }}>
                                                            <i className="bx bxs-trash-alt"></i>
                                                        </button> */}
                                            </div>
                                        )
                                    } else if (getMediaTypeFromURL(image.url) === 'video') {
                                        return (
                                            <div key={image.uuid} className="campaign-creative-img" onClick={(e) => {
                                                e.stopPropagation()
                                                setSelectedMedia(image);
                                            }}>
                                                <video className="campaign-creative-img bgc-media" src={image.url} />
                                                {/* <button type='button' className="creative-delete-btn" onClick={(e) => {
                                                            e.stopPropagation()
                                                            deleteMilestoneFile(image.uuid)
                                                        }}>
                                                            <i className="bx bxs-trash-alt"></i>
                                                        </button> */}
                                                <button type='button' className="creative-play-btn">
                                                    <i className="bx bx-play fs-3"></i>
                                                </button>
                                            </div>
                                        )
                                    }

                                })}
                        </div>
                    </Col>
                </FormGroup>
            }

            {
                milestone?.gig_type_id !== REPOST_GIGTYPE_ID && getSamplePosts().length > 0 &&
                <>
                    <div className="divider"></div>
                    <FormGroup row className='py-4 mt-3'> {/* Sample Posts */}
                        <Col sm={4} className='my-1'>
                            <div className="d-flex gap-3">
                                <div className="w-40px">
                                    <span className="icon-circle me-3"><GalleryIcon /></span>
                                </div>
                                <div className="d-flex flex-column gap-2">
                                    <Label htmlFor='name' className='form-label input-label mb-0'>
                                        References
                                    </Label>
                                    <FieldInstruction instruction={FIELDS_INSTRUCTIONS.MILESTONE.SAMPLE_POSTS} />
                                </div>
                            </div>
                        </Col>
                        <Col sm={8} className='my-1'>
                            <div className="">
                                <div className="d-flex gap-2">

                                    {/* <div
                                                                                                    className="campaign-sample-post-action-btn"
                                                                                                    onClick={e => e.stopPropagation()}
                                                                                                >
                                                                                                    <button onClick={() => setIGMediaModalOpen(true)} type='button' className="unset-all d-flex align-items-center gap-1">
                                                                                                        <span>
                                                                                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                                                                                <path d="M11.25 9C11.25 8.58579 11.5858 8.25 12 8.25C12.4142 8.25 12.75 8.58579 12.75 9L12.75 11.25H15C15.4142 11.25 15.75 11.5858 15.75 12C15.75 12.4142 15.4142 12.75 15 12.75H12.75V15C12.75 15.4142 12.4142 15.75 12 15.75C11.5858 15.75 11.25 15.4142 11.25 15L11.25 12.75H9C8.58579 12.75 8.25 12.4142 8.25 12C8.25 11.5858 8.58579 11.25 9 11.25H11.25V9Z" fill="#007AFF" />
                                                                                                                <path fillRule="evenodd" clipRule="evenodd" d="M12 1.25C17.9371 1.25 22.75 6.06294 22.75 12C22.75 17.9371 17.9371 22.75 12 22.75C6.06294 22.75 1.25 17.9371 1.25 12C1.25 6.06294 6.06294 1.25 12 1.25ZM21.25 12C21.25 6.89137 17.1086 2.75 12 2.75C6.89137 2.75 2.75 6.89137 2.75 12C2.75 17.1086 6.89137 21.25 12 21.25C17.1086 21.25 21.25 17.1086 21.25 12Z" fill="#007AFF" />
                                                                                                            </svg>
                                                                                                        </span>
                                                                                                        <span>Upload Instagram Post/Reel Link</span>
                                                                                                    </button>
                                                                                                </div> */}
                                    {/* <div
                                                    className="campaign-sample-post-action-btn"
                                                >
                                                    <button type='button' className="unset-all d-flex align-items-center gap-1">
                                                        <span>
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                                <path d="M11.25 9C11.25 8.58579 11.5858 8.25 12 8.25C12.4142 8.25 12.75 8.58579 12.75 9L12.75 11.25H15C15.4142 11.25 15.75 11.5858 15.75 12C15.75 12.4142 15.4142 12.75 15 12.75H12.75V15C12.75 15.4142 12.4142 15.75 12 15.75C11.5858 15.75 11.25 15.4142 11.25 15L11.25 12.75H9C8.58579 12.75 8.25 12.4142 8.25 12C8.25 11.5858 8.58579 11.25 9 11.25H11.25V9Z" fill="#007AFF" />
                                                                <path fillRule="evenodd" clipRule="evenodd" d="M12 1.25C17.9371 1.25 22.75 6.06294 22.75 12C22.75 17.9371 17.9371 22.75 12 22.75C6.06294 22.75 1.25 17.9371 1.25 12C1.25 6.06294 6.06294 1.25 12 1.25ZM21.25 12C21.25 6.89137 17.1086 2.75 12 2.75C6.89137 2.75 2.75 6.89137 2.75 12C2.75 17.1086 6.89137 21.25 12 21.25C17.1086 21.25 21.25 17.1086 21.25 12Z" fill="#007AFF" />
                                                            </svg>
                                                        </span>
                                                        <span>Upload PNG, MP4, HEIC</span>
                                                    </button>
                                                </div> */}
                                </div>
                            </div>
                            <div className="gallery-container mt-3">

                                {getSamplePosts().length > 0 &&
                                    getSamplePosts().map((image) => {
                                        if (getMediaTypeFromURL(image.url) === 'image') {
                                            return (<div onClick={(e) => {
                                                e.stopPropagation()
                                                setSelectedMedia(image);
                                            }} key={image.uuid} className="campaign-creative-img" style={{ backgroundImage: `url(${image.url})` }}>
                                                {/* <button type='button' className="creative-delete-btn" onClick={(e) => {
                                                                e.stopPropagation()
                                                                deleteMilestoneFile(image.uuid)
                                                            }}>
                                                                <i className="bx bxs-trash-alt"></i>
                                                            </button> */}
                                            </div>)
                                        } else if (getMediaTypeFromURL(image.url) === 'video') {
                                            return (
                                                <div onClick={(e) => {
                                                    e.stopPropagation()
                                                    setSelectedMedia(image);
                                                }} key={image.uuid} className="campaign-sample-post">
                                                    <video className="media" src={image.url} />
                                                    {/* <button type='button' className="creative-delete-btn" onClick={(e) => {
                                                                    e.stopPropagation()
                                                                    deleteMilestoneFile(image.uuid)
                                                                }}>
                                                                    <i className="bx bxs-trash-alt"></i>
                                                                </button> */}
                                                    <button type='button' className="creative-play-btn">
                                                        <i className="bx bx-play fs-3"></i>
                                                    </button>
                                                </div>
                                            )
                                        }

                                    })}
                            </div>
                        </Col>
                    </FormGroup>
                </>
            }
            <AddCampaignGig
                isOpen={addGigModalOpen}
                selectedGigId={selectedGigId!}
                gigType={getGigType(milestone?.gig_type_id)!}
                onClose={() => {
                    setAddGigModalOpen(false);
                    setSelectedGigId(null);
                }}
                campaignGigIds={milestone?.campaign_gig_ids || []}
                addCampaign={(gigId) => {
                    attachCampaignGig(gigId);
                    setAddGigModalOpen(false);
                    setSelectedGigId(null);
                }}
                campaignGigs={campaignGigs}
            />
            <AddCTA
                isOpen={addCTAModalOpen}
                onClose={() => {
                    setAddCTAModalOpen(false)
                    // setCTAToBeEdited(null);
                }}
                onAddition={(cta) => {
                    attachCampaignCTA(cta);
                    setAddCTAModalOpen(false)

                }}
                existingCTA={CTAToBeEdited}
                onUpdate={(updatedCTA) => {
                    // const specifications = { ...milestones[activeTab].specifications };
                    // specifications.cta = Array.isArray(specifications.cta) ? [...specifications.cta] : [];
                    // specifications.cta[updatedCTA.index] = updatedCTA.data;
                    // handleMilestoneChange(activeTab, 'specifications', specifications);
                    // setAddCTAModalOpen(false)
                    // setCTAToBeEdited(null);
                    // setErrors((prev) => ({ ...prev, cta: '' }))
                }}
            />
            <Modal isOpen={!!selectedMedia} toggle={() => setSelectedMedia(null)}>
                <CloseButton top='0' onClose={() => setSelectedMedia(null)} />
                <ModalBody className='p-0 overflow-hidden' style={{ borderRadius: '6px', height: '75vh' }}>
                    {
                        selectedMedia &&
                        <div className='w-100 h-100 d-flex justify-content-center align-items-center'>
                            {
                                getMediaTypeFromURL(selectedMedia.url) === 'video' &&
                                <video src={selectedMedia.url} autoPlay controls className='h-100 w-100 bgc-content' />
                            }
                            {
                                getMediaTypeFromURL(selectedMedia.url) === 'image' &&
                                <div className="h-100 w-100 bg-media bgc-content" style={{
                                    backgroundImage: `url(${selectedMedia.url})`
                                }}></div>
                            }
                            <div className="media-action">
                                <span className='text-white fs-5' onClick={() => setSelectedMedia(null)}><i className="fa-solid fa-circle-xmark"></i></span>
                            </div>
                        </div>
                    }
                </ModalBody>
            </Modal>
        </>
    )
}

export default MilestoneForm